import React, { ReactElement } from 'react';

import { useUser } from '@sdk/hook/useUser';
import { useWishlist } from '@sdk/hook/useWishlist';

import { useRouter } from 'next/router';
import { localeToSaleorQuery } from '@utils/regions';

import {
  useAddWishlistProductVariantMutation,
  useRemoveWishlistProductVariantMutation,
} from '@sdk/gqlTypes/generatedHooks';

import { Message } from '@components/atoms/Message';
import { SelectedAttributeValueInput } from '@sdk/gqlTypes/generatedTypes';
import { useIntl } from 'react-intl';

import { compareAttributes } from '@sdk/providers/utils';

interface Props {
  addedToWishlist: boolean;
  variantId: string;
  selectedProductAttributes: SelectedAttributeValueInput[];
  showMessage?: boolean;
  productId: string;
  children(props: {
    onClick: () => Promise<void>;
    added: boolean;
    loading: boolean;
    onShare?: () => void;
  }): JSX.Element;
}

export function AddToWishlist({
  addedToWishlist,
  variantId,
  selectedProductAttributes,
  children,
  showMessage = false,
  productId,
}: Props): ReactElement {
  const { items } = useWishlist();
  const intl = useIntl();
  const { user } = useUser();
  const router = useRouter();
  const locale = router.query.locale?.toString();

  const [showAddMessage, setShowAddMessage] = React.useState(false);
  const [showRemoveMessage, setShowRemoveMessage] = React.useState(false);
  const [showNotLoggedMessage, setShowNotLoggedMessage] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState([]);

  const [addWishlistProductVariantMutation] =
    useAddWishlistProductVariantMutation();
  const [removeWishlistProductVariantMutation] =
    useRemoveWishlistProductVariantMutation();

  const addOrRemoveFromWishlist = async (): Promise<void> => {
    if (!user) {
      setShowNotLoggedMessage(true);
      return;
    }
    setLoading(true);
    if (addedToWishlist) {
      const { data } = await removeWishlistProductVariantMutation({
        variables: {
          email: '',
          productAttributes: selectedProductAttributes,
          variantId,
          ...localeToSaleorQuery(locale),
        },
      });
      if (data?.wishlistRemoveVariant?.errors.length) {
        setError(data?.wishlistRemoveVariant?.errors);
      }
      // setShowAddMessage(false);
      setShowRemoveMessage(true);

      // @ analytics
      const item = items?.find(
        (o) =>
          o.variant.id === variantId &&
          compareAttributes(o.productAttributes, selectedProductAttributes)
      );
      window?.rudderanalytics?.track('Product Removed from Wishlist', {
        name: item.variant.product.productType.slug,
        product_id: item.variant.product.slug,
        variant: variantId,
      });
    } else {
      const { data } = await addWishlistProductVariantMutation({
        variables: {
          email: '',
          productAttributes: selectedProductAttributes,
          variantId,
          ...localeToSaleorQuery(locale),
        },
      });
      if (data?.wishlistAddVariant?.errors.length) {
        setError(data?.wishlistAddVariant?.errors);
      }
      // setShowRemoveMessage(false);
      setShowAddMessage(true);

      // @ analytics
      window?.rudderanalytics?.track('Product Added to Wishlist', {
        product_id: productId,
        variant: variantId,
      });
      // return Promise.resolve();
    }
    setLoading(false);
  };

  const handleRemoveMessageClose = () => {
    setShowRemoveMessage(false);
  };

  const handleAddMessageClose = () => {
    setShowAddMessage(false);
  };

  const handleNotLoggedMessageClose = () => {
    setShowNotLoggedMessage(false);
  };

  const getRemoveMessage = (): string =>
    !error.length
      ? intl.formatMessage({
          defaultMessage: 'Product removed from wishlist',
          id: 'vUDG7n',
        })
      : intl.formatMessage({
          defaultMessage: 'Error while removing product from wishlist',
          id: 'W6OcAx',
        });
  const getAddMessage = (): string =>
    !error.length
      ? intl.formatMessage({
          defaultMessage: 'Product added to wishlist',
          id: 'FqHbF7',
        })
      : intl.formatMessage({
          defaultMessage: 'Error while adding product to wishlist',
          id: '4ibWgQ',
        });

  return (
    <>
      {children({
        added: addedToWishlist,
        loading,
        onClick: addOrRemoveFromWishlist,
      })}
      {showMessage && showAddMessage && !loading && (
        <Message
          title={getAddMessage()}
          status={error.length ? 'error' : 'success'}
          onClick={handleAddMessageClose}
        />
      )}
      {showMessage && showRemoveMessage && !loading && (
        <Message
          title={getRemoveMessage()}
          status={error.length ? 'error' : 'success'}
          onClick={handleRemoveMessageClose}
        />
      )}
      {showMessage && showNotLoggedMessage && (
        <Message
          title={intl.formatMessage({
            defaultMessage:
              'Please log in to add the product to your wish list',
            id: 'zMA2Wt',
          })}
          status="error"
          onClick={handleNotLoggedMessageClose}
        />
      )}
    </>
  );
}
